import { useEffect } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $createParagraphNode,
  $createRangeSelection,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_CRITICAL,
  KEY_DOWN_COMMAND,
  PASTE_COMMAND,
  TextNode,
} from "lexical"

/**
 * This plugin ensures the editor always has content that browsers recognize as editable.
 * It solves the issue where paste doesn't appear in context menus for empty editors.
 *
 * Based on known Lexical issues:
 * - Can't paste into empty editor (GitHub issue #2308)
 * - Browser contenteditable behavior with empty elements
 */
export default function EnsureEditablePlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    // Ensure the editor always has at least one paragraph with a text node
    const ensureEditableContent = () => {
      editor.update(() => {
        const root = $getRoot()

        // Check if root is completely empty
        if (root.getChildrenSize() === 0) {
          const paragraph = $createParagraphNode()
          const textNode = $createTextNode("\u200B") // Zero-width space
          paragraph.append(textNode)
          root.append(paragraph)

          // Set selection at the beginning
          const selection = $createRangeSelection()
          selection.anchor.set(textNode.getKey(), 0, "text")
          selection.focus.set(textNode.getKey(), 0, "text")
          $setSelection(selection)
        } else {
          // Check if we have paragraphs but they're empty
          const firstChild = root.getFirstChild()
          if (firstChild && $isElementNode(firstChild) && firstChild.getTextContent() === "") {
            // Add a zero-width space text node if the paragraph is empty
            const textNode = $createTextNode("\u200B")
            firstChild.append(textNode)
          }
        }
      })
    }

    // Run on mount
    ensureEditableContent()

    // Handle paste into empty editor
    const unregisterPaste = editor.registerCommand(
      PASTE_COMMAND,
      () => {
        // Let Lexical handle the paste, but ensure we have content first
        editor.update(() => {
          const root = $getRoot()
          const selection = $getSelection()

          // If root is empty or has no selection, set up proper structure
          if (root.getChildrenSize() === 0 || !selection) {
            const paragraph = $createParagraphNode()
            root.append(paragraph)
            paragraph.select()
          }
        })

        return false // Let other handlers process
      },
      COMMAND_PRIORITY_CRITICAL
    )

    // Clean up zero-width spaces when user starts typing
    const unregisterKeyDown = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        // If user types a character (not control keys)
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              const nodes = selection.getNodes()
              nodes.forEach((node) => {
                if (node instanceof TextNode && node.getTextContent() === "\u200B") {
                  // Remove the zero-width space when user starts typing
                  node.setTextContent("")
                }
              })
            }
          })
        }
        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )

    // Monitor content changes to ensure editability
    const unregisterUpdate = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot()
        const textContent = root.getTextContent()

        // If the editor becomes completely empty, add structure back
        if (textContent === "" || root.getChildrenSize() === 0) {
          ensureEditableContent()
        }
      })
    })

    return () => {
      unregisterPaste()
      unregisterKeyDown()
      unregisterUpdate()
    }
  }, [editor])

  return null
}
